import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/docs/docs/node_modules/gatsby-theme-docz/src/base/Layout.js";
import { BannerAlertBox } from '@entur/alert';
import { ExpandablePanel } from "@entur/expand";
import { Link, ListItem, NumberedList } from '@entur/typography';
import './guides.scss';
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <h1 {...{
      "id": "search-guide"
    }}>{`Search Guide`}</h1>
    <h2 {...{
      "id": "table-of-contents"
    }}>{`Table of Contents`}</h2>
    <NumberedList className="offers-guides-toc" mdxType="NumberedList">
    <ListItem mdxType="ListItem">
        <Link href="#how-to-use-the-offers-api" mdxType="Link">How to use the Offers API</Link>
    </ListItem>
    <ListItem mdxType="ListItem">
        <Link href="#search-overview" mdxType="Link">Search Overview</Link>
        <NumberedList className="offers-guides-toc" mdxType="NumberedList">
            <ListItem mdxType="ListItem">
                <Link href="#travellers-and-user-profiles" mdxType="Link">Travellers and User Profiles</Link>
            </ListItem>
            <ListItem mdxType="ListItem">
                <Link href="#search-by-trip-pattern" mdxType="Link">Search by trip pattern</Link>
            </ListItem>
            <ListItem mdxType="ListItem">
                <Link href="#search-by-stop-places" mdxType="Link">Search by stop places</Link>
            </ListItem>
            <ListItem mdxType="ListItem">
                <Link href="#search-by-authority" mdxType="Link">Search by authority</Link>
            </ListItem>
            <ListItem mdxType="ListItem">
                <Link href="#search-by-zones" mdxType="Link">Search by zones</Link>
            </ListItem>
            <ListItem mdxType="ListItem">
                <Link href="#search-for-personalised-offers" mdxType="Link">Search for personalised offers</Link>
                <NumberedList className="offers-guides-toc" mdxType="NumberedList">
                    <ListItem mdxType="ListItem">
                        <Link href="#campaign-codes" mdxType="Link">Campaign codes</Link>
                    </ListItem>
                    <ListItem mdxType="ListItem">
                        <Link href="#personalisation-programs" mdxType="Link">Personalisation programs</Link>
                    </ListItem>
                    <ListItem mdxType="ListItem">
                        <Link href="#personnel-tickets" mdxType="Link">Personnel tickets</Link>
                    </ListItem>
                    <ListItem mdxType="ListItem">
                        <Link href="#discounted-offers" mdxType="Link">Discounted offers</Link>
                    </ListItem>
                </NumberedList>
            </ListItem>
        </NumberedList>
    </ListItem>
    <ListItem mdxType="ListItem">
        <Link href="#search-response-overview" mdxType="Link">Search Response Overview</Link>
        <NumberedList className="offers-guides-toc" mdxType="NumberedList">
            <ListItem mdxType="ListItem">
                <Link href="#unavailable-products" mdxType="Link">Unavailable Products</Link>
            </ListItem>
            <ListItem mdxType="ListItem">
                <Link href="#geographical-validity" mdxType="Link">Geographical Validity</Link>
            </ListItem>
        </NumberedList>
    </ListItem>
    <ListItem mdxType="ListItem">
        <Link href="#offline-clients" mdxType="Link">Offline clients</Link>
    </ListItem>
    </NumberedList>
    <br />
    <h2 {...{
      "id": "how-to-use-the-offers-api"
    }}>{`How to use the Offers API`}</h2>
    <p><a parentName="p" {...{
        "href": "#table-of-contents"
      }}>{`(back to Table of contents)`}</a></p>
    <p>{`Offers API provides REST-endpoints for generating offers that in turn can be used to create tickets, granting access to public transport services.
The most typical use cases start by searching for a specific trip or an area of travel,
using the `}<a parentName="p" {...{
        "href": "/pages-journeyplanner-journeyplanner"
      }}>{`Journey Planner API`}</a>{` to find the necessary information to create Offers requests.
Offers uses the definition of products defined in the `}<a parentName="p" {...{
        "href": "/pages-products-docs-intro"
      }}>{`Products API`}</a>{` to calculate prices and evaluate valid products for the requested journey.
For a more detailed guide on how to complete a sale, see the `}<a parentName="p" {...{
        "href": "/pages-sales-guides"
      }}>{`Sales guide`}</a>{`.`}</p>
    <h2 {...{
      "id": "search-overview"
    }}>{`Search Overview`}</h2>
    <p><a parentName="p" {...{
        "href": "#table-of-contents"
      }}>{`(back to Table of contents)`}</a></p>
    <p>{`Offers have four endpoints used to search for offers by different means:`}</p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "#search-by-trip-pattern"
        }}>{`Trip pattern`}</a>{` when a specific departure is known.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "#search-by-zones"
        }}>{`Zones`}</a>{` when the desired ticket should be valid for a zone or zones, but not a specific departure.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "#search-by-stop-places"
        }}>{`Stop places`}</a>{` if the desired ticket should not be associated with any specific departure, but should yield travel rights between the two stop places provided.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "#search-by-authority"
        }}>{`Authority`}</a>{` to search for offers from a specific authority. Typically, for products where geography is not a factor when calculating prices.`}</li>
    </ul>
    <p>{`All endpoints return the same `}<inlineCode parentName="p">{`OfferSummary`}</inlineCode>{` response, providing a concise overview of the offer without the underlying complexity of the product definition.
Offers are valid for 30 minutes, after which they can no longer be purchased. A response header `}<inlineCode parentName="p">{`Expires`}</inlineCode>{` is provided in the response indicating the time when the offer expires.`}</p>
    <p>{`The products that give a user a right to travel are found under `}<inlineCode parentName="p">{`preassignedProducts`}</inlineCode>{`.
Each PreassignedProduct has an attribute `}<inlineCode parentName="p">{`properties`}</inlineCode>{` shared with `}<inlineCode parentName="p">{`optionalProducts`}</inlineCode>{` that contains most of the information needed for a given product.`}</p>
    <BannerAlertBox title="Traveller combinations" variant="warning" mdxType="BannerAlertBox">
    Each request should represent an <strong>actual travelling party</strong> — the travellers in a single request should be people travelling together.
    Do not send multiple travellers with different user types to compare price categories for a single person.
    Doing so may cause incorrect recommendations, unintended group ticket logic, and increased latency.
    </BannerAlertBox>
    <p>{`All requests for all endpoints must contain `}<em parentName="p">{`at least one traveller`}</em>{`, represented by the `}<inlineCode parentName="p">{`travellers`}</inlineCode>{` field. Either `}<inlineCode parentName="p">{`age`}</inlineCode>{` or `}<inlineCode parentName="p">{`userType`}</inlineCode>{` must be set.`}</p>
    <p>{`To receive offers for optional supplement products for a traveller, the `}<inlineCode parentName="p">{`baggageTypes`}</inlineCode>{` field containing the desired type(s) must be included.`}</p>
    <p>{`To indicate that a traveller `}<em parentName="p">{`already holds a valid ticket`}</em>{` (product, discount, or entitlement), include either `}<inlineCode parentName="p">{`productIds`}</inlineCode>{` under each traveller in the `}<inlineCode parentName="p">{`travellers`}</inlineCode>{` array, or provide the `}<inlineCode parentName="p">{`existingTicket`}</inlineCode>{` object separately.
Offers returned will consider the existing ticket(s) when determining which additional products may be needed, potentially at a reduced price.
Use `}<inlineCode parentName="p">{`productIds`}</inlineCode>{` under each traveller for single- or multi-traveller requests to indicate individually owned tickets (supported by all endpoints).
For single-traveller requests that require specifying zones or service journeys, use `}<inlineCode parentName="p">{`existingTicket`}</inlineCode>{` (available only in the `}<a parentName="p" {...{
        "href": "#search-by-trip-pattern"
      }}>{`Trip pattern`}</a>{` and `}<a parentName="p" {...{
        "href": "#search-by-zones"
      }}>{`Zones`}</a>{` endpoints).`}</p>
    <p>{`For more info see `}<a parentName="p" {...{
        "href": "https://petstore.swagger.io/?url=https://api.entur.io/api-docs/offers#/offline/post-offers-v2-offline-cache-sj-stops"
      }}>{`Swagger documentation`}</a></p>
    <h3 {...{
      "id": "travellers-and-user-profiles"
    }}>{`Travellers and User Profiles`}</h3>
    <p><a parentName="p" {...{
        "href": "#table-of-contents"
      }}>{`(back to Table of contents)`}</a></p>
    <p>{`Each operator configures `}<strong parentName="p">{`User Profiles`}</strong>{` for their products — for example "Child 6-17", "Youth 18-19", "Adult 18+". These profiles define age ranges, user types, and whether proof of identity is required. When Offers receives a request, it matches the traveller against these profiles to determine which products are available and at what price.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`age`}</inlineCode>{` and `}<inlineCode parentName="p">{`userType`}</inlineCode>{` fields on a `}<inlineCode parentName="p">{`Traveller`}</inlineCode>{` control how this matching is done.`}</p>
    <BannerAlertBox title="Always send age when known" variant="info" mdxType="BannerAlertBox">
    It is <strong>recommended</strong> to always include the <code>age</code> field when the traveller's age is known.
    Without <code>age</code>, the system uses a fallback hierarchy that may match a broader profile than intended — for example, <code>userType: CHILD</code> without <code>age</code> may return products only configured for adults.
    With <code>age</code>, profiles are matched by age range instead, ensuring the correct profile is selected. Since each operator defines their own age ranges, a <code>userType</code> that is valid for one operator may not be valid for another — making <code>age</code> particularly valuable on multi-operator journeys.
    </BannerAlertBox>
    <h4 {...{
      "id": "with-age-recommended"
    }}>{`With `}<inlineCode parentName="h4">{`age`}</inlineCode>{` (recommended)`}</h4>
    <p>{`When only `}<inlineCode parentName="p">{`age`}</inlineCode>{` is provided, profiles are matched purely by age range — no fallback hierarchy is used. All profiles where the traveller's age falls within minAge/maxAge are returned, except profiles that require specific proof of identity (such as a student ID).
For example, `}<inlineCode parentName="p">{`age: 18`}</inlineCode>{` may return offers for both a youth profile (18-19) and an adult profile (18+), because both age ranges include 18. A product configured only for adults (minAge 18) will not match a traveller with `}<inlineCode parentName="p">{`age: 8`}</inlineCode>{`.`}</p>
    <p>{`If `}<inlineCode parentName="p">{`userType`}</inlineCode>{` is also provided, it enables matching against profiles that require proof of identity. For example, `}<inlineCode parentName="p">{`age: 22`}</inlineCode>{` with `}<inlineCode parentName="p">{`userType: STUDENT`}</inlineCode>{` can match a student profile that requires a student ID — with `}<inlineCode parentName="p">{`age`}</inlineCode>{` alone, this profile would be excluded. However, `}<inlineCode parentName="p">{`age: 30`}</inlineCode>{` with `}<inlineCode parentName="p">{`userType: STUDENT`}</inlineCode>{` will match an adult profile instead if the operator's student profile has a maximum age below 30.`}</p>
    <p>{`Note that adding `}<inlineCode parentName="p">{`userType`}</inlineCode>{` may also narrow results: The system filters to profiles compatible with the declared user type's fallback chain. For example, `}<inlineCode parentName="p">{`age: 18`}</inlineCode>{` alone may return both youth and adult offers, but `}<inlineCode parentName="p">{`age: 18`}</inlineCode>{` with `}<inlineCode parentName="p">{`userType: STUDENT`}</inlineCode>{` returns only the adult offer — because youth is not in the student fallback chain.`}</p>
    <p><inlineCode parentName="p">{`userType`}</inlineCode>{` is also used by the `}<a parentName="p" {...{
        "href": "#search-for-personalised-offers"
      }}>{`Personalisation`}</a>{` service to evaluate whether a traveller qualifies for discounts. Without `}<inlineCode parentName="p">{`userType`}</inlineCode>{`, personalised discounts may not be applied correctly.`}</p>
    <h4 {...{
      "id": "with-usertype-only"
    }}>{`With `}<inlineCode parentName="h4">{`userType`}</inlineCode>{` only`}</h4>
    <p>{`Without `}<inlineCode parentName="p">{`age`}</inlineCode>{`, the system cannot check age ranges, so the `}<strong parentName="p">{`fallback hierarchy`}</strong>{` alone determines which profile is matched. Not all products are configured with profiles for every user type — for example, an operator may offer single tickets for both children and adults, but only configure period tickets with an adult profile. If you request `}<inlineCode parentName="p">{`userType: CHILD`}</inlineCode>{` for a product that only has an adult profile, the system falls back to the adult profile.`}</p>
    <p>{`For each product, the system checks whether the traveller matches any of the product's configured profiles. If no direct match is found, the fallback chain determines which other profile types the traveller is allowed to match:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`userType`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Fallback order`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`CHILD`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`CHILD → YOUTH → ADULT`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`YOUTH`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`YOUTH → ADULT`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`STUDENT`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`STUDENT → ADULT`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`SENIOR`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`SENIOR → ADULT`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`MILITARY`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`MILITARY → ADULT`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`INFANT`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`INFANT`}</td>
        </tr>
      </tbody>
    </table>
    <p>{`ADULT is the final fallback for most user types, since it is always valid to travel at full price — though not always the most suitable profile for the traveller. This can lead to unintended results — consider an operator that offers a single ticket configured for both Child (6-17) and Adult (18+), and a 7-day pass configured for Adult (18+) only:`}</p>
    <ExpandablePanel title="Example: without age — child receives adult product via fallback" mdxType="ExpandablePanel">
  <pre>{`{
    "travellers": [{ "userType": "CHILD" }]
  }`}</pre>
    </ExpandablePanel>
    <p>{`The child receives offers for `}<strong parentName="p">{`both`}</strong>{` products. The 7-day pass has no Child profile, so the system falls back to Adult — returning an adult product for a child traveller.`}</p>
    <ExpandablePanel title="Example: with age — only matching products are returned" mdxType="ExpandablePanel">
  <pre>{`{
    "travellers": [{ "userType": "CHILD", "age": 8 }]
  }`}</pre>
    </ExpandablePanel>
    <p>{`With `}<inlineCode parentName="p">{`age: 8`}</inlineCode>{`, the child receives only the single ticket. The 7-day pass has an Adult profile (minAge 18), and age 8 is outside the range — so no match is made.`}</p>
    <br />
    <h3 {...{
      "id": "search-by-trip-pattern"
    }}>{`Search by trip pattern`}</h3>
    <p><a parentName="p" {...{
        "href": "#table-of-contents"
      }}>{`(back to Table of contents)`}</a>{` `}<br />{`
`}<a parentName="p" {...{
        "href": "https://petstore.swagger.io/?url=https://api.entur.io/api-docs/offers#/external/post-offers-v2-search-trip-pattern"
      }}>{`Swagger documentation`}</a></p>
    <p>{`When a trip is found using the `}<a parentName="p" {...{
        "href": "/pages-journeyplanner-journeyplanner"
      }}>{`Journey Planner API`}</a>{`, legs for the request to
the Offers API can be constructed by extracting `}<inlineCode parentName="p">{`travelDate`}</inlineCode>{`, `}<inlineCode parentName="p">{`fromStopPlaceId`}</inlineCode>{`, `}<inlineCode parentName="p">{`toStopPlaceId`}</inlineCode>{`, and `}<inlineCode parentName="p">{`serviceJourneyId`}</inlineCode>{` from the Journey Planner response.`}</p>
    <h4 {...{
      "id": "examplerequests-to-journey-planner"
    }}>{`ExampleRequests to Journey Planner`}</h4>
    <ExpandablePanel title="ExampleRequest Journey Planner trip search request" mdxType="ExpandablePanel">
    <pre>{`{
  trip(
    from: {
      place: "NSR:StopPlace:337"
    },
    to: {
      place: "NSR:StopPlace:11"
    },
    modes: {
      transportModes: [{
        transportMode: rail
      }]
    }
    numTripPatterns: 1
  ) {
    tripPatterns {
      legs {
        datedServiceJourney {
          operatingDay
          serviceJourney {
            id
          }
        }
        fromPlace {
          quay {
            stopPlace {
              name
              id
            }
          }
        }
        toPlace {
          quay {
            stopPlace {
              id
            }
          }
        }
      }
    }
  }
}`}</pre>
    </ExpandablePanel>
    <ExpandablePanel title="ExampleRequest Journey Planner trip search response" mdxType="ExpandablePanel">
    <pre>{`{
  "data": {
    "trip": {
      "tripPatterns": [
        {
          "legs": [
            {
              "datedServiceJourney": {
                "operatingDay": "2024-04-23",
                "serviceJourney": {
                  "id": "ABC:ServiceJourney:123"
                }
              },
              "fromPlace": {
                "quay": {
                  "stopPlace": {
                    "name": "Oslo S",
                    "id": "NSR:StopPlace:337"
                  }
                }
              },
              "toPlace": {
                "quay": {
                  "stopPlace": {
                    "id": "NSR:StopPlace:11"
                  }
                }
              }
            }
          ]
        }
      ]
    }
  }
}`}</pre>
    </ExpandablePanel>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`travelDate`}</inlineCode>{` is the operatingDay of the DatedServiceJourney representing the specific trip.
Note that the date of embarkation at the requested from stop is not necessarily the same as the `}<inlineCode parentName="li">{`travelDate`}</inlineCode>{`; typically for ServiceJourneys that starts before, and crosses midnight.
`}<inlineCode parentName="li">{`operatingDay`}</inlineCode>{` of the referred `}<inlineCode parentName="li">{`DatedServiceJourney`}</inlineCode>{` will always point at the correct date, and for ServiceJourneys without a DatedServiceJourney,
the date of the first estimated call is typically correct.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`fromStopPlaceId`}</inlineCode>{` is the id of the `}<inlineCode parentName="li">{`StopPlace`}</inlineCode>{` where the leg starts`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`toStopPlaceId`}</inlineCode>{` is the id of the `}<inlineCode parentName="li">{`StopPlace`}</inlineCode>{` where the leg ends`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`serviceJourneyId`}</inlineCode>{` is the id of the `}<inlineCode parentName="li">{`ServiceJourney`}</inlineCode>{` that in combination with `}<inlineCode parentName="li">{`travelDate`}</inlineCode>{` represents the specific trip`}</li>
    </ul>
    <p>{`A request can contain multiple legs if the trip spans multiple means of transport.`}</p>
    <h4 {...{
      "id": "examplerequests-to-searchtrip-pattern"
    }}>{`ExampleRequests to search/trip-pattern`}</h4>
    <p>{`A basic request to the trip-pattern endpoint may contain `}<inlineCode parentName="p">{`tripPattern`}</inlineCode>{` (including `}<inlineCode parentName="p">{`legs`}</inlineCode>{`), and `}<inlineCode parentName="p">{`travellers`}</inlineCode>{` (including `}<inlineCode parentName="p">{`userType`}</inlineCode>{` or `}<inlineCode parentName="p">{`age`}</inlineCode>{`).`}</p>
    <ExpandablePanel title="ExampleRequest to search/trip-pattern" mdxType="ExpandablePanel">
    <pre>
    {`{
  "tripPattern": {
    "legs": [
      {
        "travelDate": "2022-03-16",
        "fromStopPlaceId": "NSR:StopPlace:659",
        "toStopPlaceId": "NSR:StopPlace:507",
        "serviceJourneyId": "ABC:ServiceJourney:123"
      }
    ]
  },
  "travellers": [
    {
      "userType": "ADULT",
      "baggageTypes":[
        "BICYCLE"
      ]
    }
  ]
}`}
    </pre>
    </ExpandablePanel>
    <ExpandablePanel title="ExampleRequest to search/trip-pattern with EntitlementProduct specified in productIds" mdxType="ExpandablePanel">
    <pre>
    {`{
  "travellers": [
    {
      "id": "UNIQUE_IDENTIFIER",
      "userType": "ADULT",
      "productIds": [
        "ENT:EntitlementProduct:PersonnelTicket"
      ]
    }
  ],
  "tripPattern": {
    "legs": [
      {
        "travelDate": "2025-08-15",
        "fromStopPlaceId": "NSR:StopPlace:337",
        "toStopPlaceId": "NSR:StopPlace:269",
        "serviceJourneyId": "ABC:ServiceJourney:123"
      }
    ]
  }
}`}
    </pre>
    </ExpandablePanel>
    <ExpandablePanel title="ExampleRequest to search/trip-pattern with 30DayPass (valid for Zones ABC:FareZone:4, ABC:FareZone:3, ABC:FareZone:2) specified in existingTicket" mdxType="ExpandablePanel">
    <pre>
    {`{
  "travellers": [
    {
      "id": "UNIQUE_IDENTIFIER",
      "userType": "ADULT"
    }
  ],
  "tripPattern": {
    "legs": [
      {
        "travelDate": "2025-08-15",
        "fromStopPlaceId": "NSR:StopPlace:337",
        "toStopPlaceId": "NSR:StopPlace:269",
        "serviceJourneyId": "ABC:ServiceJourney:123"
      }
    ]
  },
  "existingTicket": {
      "id": "ABC:PreassignedFareProduct:Ruter30Days",
      "zones": [
          "ABC:FareZone:4",
          "ABC:FareZone:3",
          "ABC:FareZone:2"
      ]
  }
}`}
    </pre>
    </ExpandablePanel>
    <br />
    <p>{`If more than one traveller is travelling together, the `}<inlineCode parentName="p">{`travellers`}</inlineCode>{` attribute can contain multiple `}<inlineCode parentName="p">{`Traveller`}</inlineCode>{` objects,
representing each traveller with their respective `}<inlineCode parentName="p">{`userType`}</inlineCode>{` and/or `}<inlineCode parentName="p">{`age`}</inlineCode>{`.
Do `}<strong parentName="p">{`not`}</strong>{` use grouping as a shortcut when searching for offers for multiple travellers unless they are expected to be travelling together.
For requests with groups of travellers, the `}<inlineCode parentName="p">{`groupConfig`}</inlineCode>{` attribute can be used to specify the number of compartments desired for a group (`}<inlineCode parentName="p">{`numberOfCompartments`}</inlineCode>{`).
This will only apply to products that are sold as group tickets and yields a compartment.`}</p>
    <ExpandablePanel title="ExampleRequest to search/trip-pattern with 4 travellers in 2 compartments" mdxType="ExpandablePanel">
    <pre>
    {`{
  "travellers": [
    {
      "id": "A",
      "userType": "ADULT",
      "age": 18
    },
    {
      "id": "B",
      "userType": "CHILD"
    },
    {
      "id": "C",
      "userType": "INFANT",
    },
    {
      "id": "D",
      "userType": "CHILD",
      "age": 12
    }
  ],
  "tripPattern": {
    "legs": [
      {
        "travelDate": "2022-02-24",
        "fromStopPlaceId": "NSR:StopPlace:337",
        "toStopPlaceId": "NSR:StopPlace:548",
        "serviceJourneyId": "ABC:ServiceJourney:123"
      }
    ]
  },
  "groupConfig": {
      "numberOfCompartments": 2
  }
}`}
    </pre>
    </ExpandablePanel>
    <h4 {...{
      "id": "exampleresponses-from-searchtrip-pattern"
    }}>{`ExampleResponses from search/trip-pattern`}</h4>
    <ExpandablePanel title="ExampleResponse from search/trip-pattern" mdxType="ExpandablePanel">
    <pre>
    {`{
  "tripPatternId": "9f527bd5-23db-4a05-bb4a-a75fc5451a27",
  "offers": [
    {
      "id": "ea10daf8-3d77-4a01-b9b4-81260ce8eefe",
      "name": "Standard non-refundable",
      "description": "",
      "price": {
        "amount": "639.00",
        "currency": "NOK"
      },
      "geographicalValidity": {
        "serviceJourneys": [
          {
            "datedServiceJourneyId": "ABC:DatedServiceJourney:1234_ABC-DEF_25-05-25",
            "serviceJourneyId": "ABC:ServiceJourney:123"
          }
        ],
        "pointToPointValidity": {
          "fromPlace": "NSR:StopPlace:659",
          "toPlace": "NSR:StopPlace:507"
        }
      },
      "preassignedProducts": [
        {
          "id": "ABC:PreassignedFareProduct:Standard",
          "version": "ABC:Version:PFP-Standard-2",
          "name": "Standard non-refundable",
          "description": "",
          "properties": {
            "isExchangeable": false,
            "isRefundable": false,
            "accommodations": [
              {
                "serviceJourney": {
                  "datedServiceJourneyId": "ABC:DatedServiceJourney:1234_ABC-DEF_25-05-25",
                  "serviceJourneyId": "ABC:ServiceJourney:123"
                },
                "accommodation": []
              }
            ],
            "reservingRequirements": [
              {
                "serviceJourney": {
                  "datedServiceJourneyId": "ABC:DatedServiceJourney:1234_ABC-DEF_25-05-25",
                  "serviceJourneyId": "ABC:ServiceJourney:123"
                },
                "seatMapIsAvailable": true
              },
              {
                "serviceJourney": {
                  "datedServiceJourneyId": "ABC:DatedServiceJourney:1234_ABC-DEF_25-05-25",
                  "serviceJourneyId": "ABC:ServiceJourney:123"
                },
                "reservingRequirement": "RESERVATIONS_COMPULSORY",
                "seatMapIsAvailable": false
              }
            ],
            "userProfileId": "ABC:UserProfile:Adult",
            "userProfileVersion": "ABC:Version:UPAdult-2020-1",
            "organisation": {
              "id": "ENT:Authority:ENT",
              "name": "Entur"
            },
            "fareClasses": [
              "STANDARD_CLASS"
            ]
          },
          "geographicalValidity": {
            "serviceJourneys": [
              {
                "datedServiceJourneyId": "ABC:DatedServiceJourney:1234_ABC-DEF_25-05-25",
                "serviceJourneyId": "ABC:ServiceJourney:123"
              }
            ],
            "pointToPointValidity": {
              "fromPlace": "NSR:StopPlace:659",
              "toPlace": "NSR:StopPlace:507"
            }
          }
        }
      ],
      "optionalProductsSelectableIds": [
        "keMdoG",
        "cB4AAY",
        "b0f6UI",
        "wC9t6m",
        "eIXy0B",
        "6tfKdb",
        "Utl3ls",
        "5BKCG8"
      ],
      "travellerMapping": [
        {
          "travellerIds": [
            "ad37276d-2a32-4d84-8c2f-08dc30f05cd6"
          ],
          "maxNumberOfTravellers": 1,
          "minNumberOfTravellers": 1,
          "userType": "ADULT"
        }
      ],
      "available": 68
    } ...
  ],
  "optionalProducts": [
    {
      "id": "ABC:SupplementProduct:BikeReservation",
      "version": "ABC:Version:SUP-BikeReservation-1",
      "selectableId": "keMdoG",
      "name": "Bicycle",
      "description": "There are reserved places for bicycles on the longer regional trains.",
      "price": {
        "amount": "215.00",
        "currency": "NOK"
      },
      "properties": {
        "accommodations": [
          {
            "serviceJourney": {
              "datedServiceJourneyId": "ABC:DatedServiceJourney:1234_ABC-DEF_25-05-25",
              "serviceJourneyId": "ABC:ServiceJourney:123"
            },
            "accommodation": []
          }
        ],
        "baggageTypes": [
          "BICYCLE"
        ],
        "reservingRequirements": [
          {
            "serviceJourney": {
              "datedServiceJourneyId": "ABC:DatedServiceJourney:1234_ABC-DEF_25-05-25",
              "serviceJourneyId": "ABC:ServiceJourney:123"
            },
            "reservingRequirement": "RESERVATIONS_COMPULSORY",
            "seatMapIsAvailable": false
          }
        ],
        "organisation": {
          "id": "ENT:Authority:ENT",
          "name": "Entur"
        }
      },
      "geographicalValidity": {
        "serviceJourneys": [
          {
            "datedServiceJourneyId": "ABC:DatedServiceJourney:1234_ABC-DEF_25-05-25",
            "serviceJourneyId": "ABC:ServiceJourney:123"
          }
        ],
        "pointToPointValidity": {
          "fromPlace": "NSR:StopPlace:659",
          "toPlace": "NSR:StopPlace:507"
        }
      },
      "available": 5
    } ...
  ],
  "unavailableProducts": []
}`}
    </pre>
    </ExpandablePanel>
    <ExpandablePanel title="ExampleResponse from search/trip-pattern with EntitlementProduct specified in productIds" mdxType="ExpandablePanel">
    <pre>
    {`{
    "tripPatternId": "abfdef30-9d9f-43f2-aed5-61ec32991692",
    "offers": [
        {
            "id": "d6a56fd7-98f4-41b4-b8ba-de52112ef3df",
            "name": "Enkeltbillett",
            "description": "Enkeltbillett",
            "price": {
                "amount": "129.00",
                "currency": "NOK"
            },
            "availableFulfillmentMethods": [
                {
                    "id": "ENT:FulfilmentMethod:IdBasert",
                    "version": "ENT:Version:FM-IdBasert-1",
                    "distributionRights": [
                        "SELL",
                        "REFUND"
                    ]
                },
                {
                    "id": "ENT:FulfilmentMethod:FysiskBillett",
                    "version": "ENT:Version:FM-FysiskBillett-1",
                    "distributionRights": [
                        "SELL",
                        "REFUND"
                    ]
                }
            ],
            "geographicalValidity": {
                "serviceJourneys": [
                    {
                        "datedServiceJourneyId": "ABC:DatedServiceJourney:1234_ABC-DEF_25-05-25",
                        "serviceJourneyId": "ABC:ServiceJourney:123"
                    }
                ],
                "zonalValidity": {
                    "fareZones": [
                        "ABC:FareZone:4",
                        "ABC:FareZone:3",
                        "ABC:FareZone:2",
                        "ABC:FareZone:1"
                    ]
                }
            },
            "preassignedProducts": [
                {
                    "id": "ABC:PreassignedFareProduct:58aec6a8",
                    "version": "ABC:Version:FP-f1d831f7-bcef-49bb-8779-ea6266da91e2",
                    "name": "Enkeltbillett",
                    "description": "",
                    "properties": {
                        "isExchangeable": false,
                        "isRefundable": false,
                        "accommodations": [
                            {
                                "serviceJourney": {
                                    "datedServiceJourneyId": "ABC:DatedServiceJourney:1234_ABC-DEF_25-05-25",
                                    "serviceJourneyId": "ABC:ServiceJourney:123"
                                },
                                "accommodation": []
                            }
                        ],
                        "reservingRequirements": [
                            {
                                "serviceJourney": {
                                    "datedServiceJourneyId": "ABC:DatedServiceJourney:1234_ABC-DEF_25-05-25",
                                    "serviceJourneyId": "ABC:ServiceJourney:123"
                                },
                                "reservingRequirement": "NO_RESERVATIONS_POSSIBLE",
                                "seatMapIsAvailable": false
                            }
                        ],
                        "userProfileSummary": {
                            "id": "ABC:UserProfile:Adult",
                            "version": "ABC:Version:UP-Adult",
                            "name": "Voksen",
                            "userType": "ADULT"
                        },
                        "organisation": {
                            "id": "ENT:Authority:ENT",
                            "name": "Entur"
                        },
                        "duration": "PT2H30M",
                        "fareClasses": [
                            "STANDARD_CLASS"
                        ],
                        "durationType": "SINGLE_TRIP"
                    },
                    "geographicalValidity": {
                        "serviceJourneys": [
                            {
                                "datedServiceJourneyId": "ABC:DatedServiceJourney:1234_ABC-DEF_25-05-25",
                                "serviceJourneyId": "ABC:ServiceJourney:123"
                            }
                        ],
                        "zonalValidity": {
                            "fareZones": [
                                "ABC:FareZone:4",
                                "ABC:FareZone:3",
                                "ABC:FareZone:2",
                                "ABC:FareZone:1"
                            ]
                        }
                    }
                }
            ],
            "optionalProductsSelectableIds": [],
            "travellerMapping": [
                {
                    "travellerIds": [
                        "UNIQUE_IDENTIFIER"
                    ],
                    "maxNumberOfTravellers": 1,
                    "minNumberOfTravellers": 1,
                    "userType": "ADULT"
                }
            ]
        },
        {
            "id": "271e9771-50a6-4c79-89a1-a7b4d76463bd",
            "name": "Enkeltbillett",
            "description": "Enkeltbillett",
            "price": {
                "amount": "0.00",
                "currency": "NOK"
            },
            "availableFulfillmentMethods": [
                {
                    "id": "ENT:FulfilmentMethod:IdBasert",
                    "version": "ENT:Version:FM-IdBasert-1",
                    "distributionRights": [
                        "SELL",
                        "REFUND"
                    ]
                },
                {
                    "id": "ENT:FulfilmentMethod:FysiskBillett",
                    "version": "ENT:Version:FM-FysiskBillett-1",
                    "distributionRights": [
                        "SELL",
                        "REFUND"
                    ]
                }
            ],
            "geographicalValidity": {
                "serviceJourneys": [
                    {
                        "datedServiceJourneyId": "ABC:DatedServiceJourney:1234_ABC-DEF_25-05-25",
                        "serviceJourneyId": "ABC:ServiceJourney:123"
                    }
                ],
                "zonalValidity": {
                    "fareZones": [
                        "ABC:FareZone:4",
                        "ABC:FareZone:3",
                        "ABC:FareZone:2",
                        "ABC:FareZone:1"
                    ]
                }
            },
            "preassignedProducts": [
                {
                    "id": "ABC:PreassignedFareProduct:58aec6a8",
                    "version": "ABC:Version:FP-f1d831f7-bcef-49bb-8779-ea6266da91e2",
                    "name": "Enkeltbillett",
                    "description": "",
                    "properties": {
                        "isExchangeable": false,
                        "isRefundable": false,
                        "accommodations": [
                            {
                                "serviceJourney": {
                                    "datedServiceJourneyId": "ABC:DatedServiceJourney:1234_ABC-DEF_25-05-25",
                                    "serviceJourneyId": "ABC:ServiceJourney:123"
                                },
                                "accommodation": []
                            }
                        ],
                        "reservingRequirements": [
                            {
                                "serviceJourney": {
                                    "datedServiceJourneyId": "ABC:DatedServiceJourney:1234_ABC-DEF_25-05-25",
                                    "serviceJourneyId": "ABC:ServiceJourney:123"
                                },
                                "reservingRequirement": "NO_RESERVATIONS_POSSIBLE",
                                "seatMapIsAvailable": false
                            }
                        ],
                        "userProfileSummary": {
                            "id": "ABC:UserProfile:Adult",
                            "version": "ABC:Version:UP-Adult",
                            "name": "Voksen",
                            "userType": "ADULT"
                        },
                        "organisation": {
                            "id": "ENT:Authority:ENT",
                            "name": "Entur"
                        },
                        "duration": "PT2H30M",
                        "fareClasses": [
                            "STANDARD_CLASS"
                        ],
                        "durationType": "SINGLE_TRIP"
                    },
                    "geographicalValidity": {
                        "serviceJourneys": [
                            {
                                "datedServiceJourneyId": "ABC:DatedServiceJourney:1234_ABC-DEF_25-05-25",
                                "serviceJourneyId": "ABC:ServiceJourney:123"
                            }
                        ],
                        "zonalValidity": {
                            "fareZones": [
                                "ABC:FareZone:4",
                                "ABC:FareZone:3",
                                "ABC:FareZone:2",
                                "ABC:FareZone:1"
                            ]
                        }
                    },
                    "discountRight": {
                        "id": "ENT:SaleDiscountRight:Personalbillett",
                        "version": "ENT:Version:FP-aa91bbdb-d8fd-4bb0-a106-4ed9ee26b702",
                        "price": {
                            "amount": "-129.00",
                            "currency": "NOK"
                        },
                        "name": "Personalbillettrabatt ",
                        "description": "Personalbillettrabatt ",
                        "originatingFromProductId": "ENT:EntitlementProduct:PersonnelTicket"
                    }
                }
            ],
            "optionalProductsSelectableIds": [],
            "travellerMapping": [
                {
                    "travellerIds": [
                        "UNIQUE_IDENTIFIER"
                    ],
                    "maxNumberOfTravellers": 1,
                    "minNumberOfTravellers": 1,
                    "userType": "ADULT"
                }
            ]
        }
    ]
}`}
    </pre>
    </ExpandablePanel>
    <ExpandablePanel title="ExampleResponse from search/trip-pattern with 30DayPass (valid for Zones ABC:FareZone:4, ABC:FareZone:3, ABC:FareZone:2) specified in existingTicket" mdxType="ExpandablePanel">
    <pre>
    {`{
    "tripPatternId": "cf03960b-2076-48d4-9770-1015954538dc",
    "offers": [
        {
            "id": "1940065b-2285-4faa-a1a7-6a6ff847cfdf",
            "name": "Enkeltbillett",
            "description": "Enkeltbillett",
            "price": {
                "amount": "44.00",
                "currency": "NOK"
            },
            "availableFulfillmentMethods": [
                {
                    "id": "ENT:FulfilmentMethod:IdBasert",
                    "version": "ENT:Version:FM-IdBasert-1",
                    "distributionRights": [
                        "SELL",
                        "REFUND"
                    ]
                },
                {
                    "id": "ENT:FulfilmentMethod:FysiskBillett",
                    "version": "ENT:Version:FM-FysiskBillett-1",
                    "distributionRights": [
                        "SELL",
                        "REFUND"
                    ]
                }
            ],
            "geographicalValidity": {
                "serviceJourneys": [
                    {
                        "datedServiceJourneyId": "ABC:DatedServiceJourney:1234_ABC-DEF_25-05-25",
                        "serviceJourneyId": "ABC:ServiceJourney:123"
                    }
                ],
                "zonalValidity": {
                    "fareZones": [
                        "ABC:FareZone:1"
                    ]
                }
            },
            "preassignedProducts": [
                {
                    "id": "ABC:PreassignedFareProduct:58aec6a8",
                    "version": "ABC:Version:FP-f1d831f7-bcef-49bb-8779-ea6266da91e2",
                    "name": "Enkeltbillett",
                    "description": "",
                    "properties": {
                        "isExchangeable": false,
                        "isRefundable": false,
                        "accommodations": [
                            {
                                "serviceJourney": {
                                    "datedServiceJourneyId": "ABC:DatedServiceJourney:1234_ABC-DEF_25-05-25",
                                    "serviceJourneyId": "ABC:ServiceJourney:123"
                                },
                                "accommodation": []
                            }
                        ],
                        "reservingRequirements": [
                            {
                                "serviceJourney": {
                                    "datedServiceJourneyId": "ABC:DatedServiceJourney:1234_ABC-DEF_25-05-25",
                                    "serviceJourneyId": "ABC:ServiceJourney:123"
                                },
                                "reservingRequirement": "NO_RESERVATIONS_POSSIBLE",
                                "seatMapIsAvailable": false
                            }
                        ],
                        "userProfileSummary": {
                            "id": "ABC:UserProfile:Adult",
                            "version": "ABC:Version:UP-Adult",
                            "name": "Voksen",
                            "userType": "ADULT"
                        },
                        "organisation": {
                            "id": "ENT:Authority:ENT",
                            "name": "Entur"
                        },
                        "duration": "PT1H",
                        "fareClasses": [
                            "STANDARD_CLASS"
                        ],
                        "durationType": "SINGLE_TRIP"
                    },
                    "geographicalValidity": {
                        "serviceJourneys": [
                            {
                                "datedServiceJourneyId": "ABC:DatedServiceJourney:1234_ABC-DEF_25-05-25",
                                "serviceJourneyId": "ABC:ServiceJourney:123"
                            }
                        ],
                        "zonalValidity": {
                            "fareZones": [
                                "ABC:FareZone:1"
                            ]
                        }
                    }
                }
            ],
            "optionalProductsSelectableIds": [],
            "travellerMapping": [
                {
                    "travellerIds": [
                        "UNIQUE_IDENTIFIER"
                    ],
                    "maxNumberOfTravellers": 1,
                    "minNumberOfTravellers": 1,
                    "userType": "ADULT"
                }
            ]
        }
    ]
}`}
    </pre>
    </ExpandablePanel>
    <ExpandablePanel title="ExampleResponse from search/trip-pattern with 4 travellers in 2 compartments" mdxType="ExpandablePanel">
    <pre>
    {`{
    "tripPatternId": "f56da8ad-98d3-4ec8-b3d0-b82fed108158",
    "offers": [
        {
            "id": "68b49f9d-7334-4279-ab61-437d08eca977",
            "name": "Flex",
            "description": "",
            "price": {
                "amount": "279.00",
                "currency": "NOK"
            },
            "geographicalValidity": {
                "serviceJourneys": [
                    {
                        "datedServiceJourneyId": "ABC:DatedServiceJourney:1234_ABC-DEF_25-05-25",
                        "serviceJourneyId": "ABC:ServiceJourney:123"
                    }
                ],
                "pointToPointValidity": {
                    "fromPlace": "NSR:StopPlace:337",
                    "toPlace": "NSR:StopPlace:548"
                }
            },
            "preassignedProducts": [
                {
                    "id": "VYT:PreassignedFareProduct:Flex",
                    "version": "VYT:Version:PFP-Flex-1",
                    "name": "Flex",
                    "description": "",
                    "properties": {
                        "isExchangeable": true,
                        "isRefundable": false,
                        "accommodations": [
                            {
                                "serviceJourney": {
                                    "datedServiceJourneyId": "ABC:DatedServiceJourney:1234_ABC-DEF_25-05-25",
                                    "serviceJourneyId": "ABC:ServiceJourney:123"
                                },
                                "accommodation": [
                                    "SEATING"
                                ]
                            }
                        ],
                        "reservingRequirements": [
                            {
                                "serviceJourney": {
                                    "datedServiceJourneyId": "ABC:DatedServiceJourney:1234_ABC-DEF_25-05-25",
                                    "serviceJourneyId": "ABC:ServiceJourney:123"
                                },
                                "reservingRequirement": "RESERVATIONS_COMPULSORY",
                                "seatMapIsAvailable": true
                            }
                        ],
                        "userProfileId": "ABC:UserProfile:Adult",
                        "userProfileVersion": "ABC:Version:UP-Adult",
                        "organisation": {
                            "id": "ENT:Authority:ENT",
                            "name": "Entur"
                        },
                        "fareClasses": [
                            "STANDARD_CLASS"
                        ]
                    },
                    "geographicalValidity": {
                        "serviceJourneys": [
                            {
                                "datedServiceJourneyId": "ABC:DatedServiceJourney:1234_ABC-DEF_25-05-25",
                                "serviceJourneyId": "ABC:ServiceJourney:123"
                            }
                        ],
                        "pointToPointValidity": {
                            "fromPlace": "NSR:StopPlace:337",
                            "toPlace": "NSR:StopPlace:548"
                        }
                    }
                }
            ],
            "travellerMapping": [
                {
                    "travellerIds": [
                        "A",
                        "B",
                        "C",
                        "D"
                    ],
                    "maxNumberOfTravellers": 1,
                    "minNumberOfTravellers": 1,
                    "userType": "ADULT"
                }
            ],
            "available": 2
        },
        {
            "id": "acef5e44-5a02-4136-ab6d-5aaa7d0233e2",
            "name": "Sove",
            "description": "",
            "price": {
                "amount": "999.00",
                "currency": "NOK"
            },
            "geographicalValidity": {
                "serviceJourneys": [
                    {
                        "datedServiceJourneyId": "ABC:DatedServiceJourney:1234_ABC-DEF_25-05-25",
                        "serviceJourneyId": "ABC:ServiceJourney:123"
                    }
                ],
                "pointToPointValidity": {
                    "fromPlace": "NSR:StopPlace:337",
                    "toPlace": "NSR:StopPlace:548"
                }
            },
            "preassignedProducts": [
                {
                    "id": "VYT:PreassignedFareProduct:Sleep",
                    "version": "VYT:Version:PFP-Sleep-1",
                    "name": "Sove",
                    "description": "",
                    "properties": {
                        "isExchangeable": true,
                        "isRefundable": false,
                        "accommodations": [
                            {
                                "serviceJourney": {
                                    "datedServiceJourneyId": "ABC:DatedServiceJourney:1234_ABC-DEF_25-05-25",
                                    "serviceJourneyId": "ABC:ServiceJourney:123"
                                },
                                "accommodation": [
                                    "SLEEPER"
                                ]
                            }
                        ],
                        "reservingRequirements": [
                            {
                                "serviceJourney": {
                                    "datedServiceJourneyId": "ABC:DatedServiceJourney:1234_ABC-DEF_25-05-25",
                                    "serviceJourneyId": "ABC:ServiceJourney:123"
                                },
                                "reservingRequirement": "RESERVATIONS_COMPULSORY",
                                "seatMapIsAvailable": false
                            }
                        ],
                        "organisation": {
                            "id": "ENT:Authority:ENT",
                            "name": "Entur"
                        },
                        "group": {
                            "id": "VYT:GroupTicket:Sleep",
                            "version": "VYT:Version:GT-Sleep-1",
                            "description": "",
                            "userProfiles": []
                        },
                        "fareClasses": [
                            "PREMIUM_CLASS"
                        ]
                    },
                    "geographicalValidity": {
                        "serviceJourneys": [
                            {
                                "datedServiceJourneyId": "ABC:DatedServiceJourney:1234_ABC-DEF_25-05-25",
                                "serviceJourneyId": "ABC:ServiceJourney:123"
                            }
                        ],
                        "pointToPointValidity": {
                            "fromPlace": "NSR:StopPlace:337",
                            "toPlace": "NSR:StopPlace:548"
                        }
                    }
                }
            ],
            "travellerMapping": [
                {
                    "travellerIds": [
                        "B",
                        "D"
                    ],
                    "maxNumberOfTravellers": 2,
                    "minNumberOfTravellers": 2,
                    "userType": "ADULT"
                }
            ],
            "available": 5
        },
        {
            "id": "9e71c4ee-2173-47d9-882d-d1d6d38f736f",
            "name": "Sove",
            "description": "",
            "price": {
                "amount": "999.00",
                "currency": "NOK"
            },
            "geographicalValidity": {
                "serviceJourneys": [
                    {
                        "datedServiceJourneyId": "ABC:DatedServiceJourney:1234_ABC-DEF_25-05-25",
                        "serviceJourneyId": "ABC:ServiceJourney:123"
                    }
                ],
                "pointToPointValidity": {
                    "fromPlace": "NSR:StopPlace:337",
                    "toPlace": "NSR:StopPlace:548"
                }
            },
            "preassignedProducts": [
                {
                    "id": "VYT:PreassignedFareProduct:Sleep",
                    "version": "VYT:Version:PFP-Sleep-1",
                    "name": "Sove",
                    "description": "",
                    "properties": {
                        "isExchangeable": true,
                        "isRefundable": false,
                        "accommodations": [
                            {
                                "serviceJourney": {
                                    "datedServiceJourneyId": "ABC:DatedServiceJourney:1234_ABC-DEF_25-05-25",
                                    "serviceJourneyId": "ABC:ServiceJourney:123"
                                },
                                "accommodation": [
                                    "SLEEPER"
                                ]
                            }
                        ],
                        "reservingRequirements": [
                            {
                                "serviceJourney": {
                                    "datedServiceJourneyId": "ABC:DatedServiceJourney:1234_ABC-DEF_25-05-25",
                                    "serviceJourneyId": "ABC:ServiceJourney:123"
                                },
                                "reservingRequirement": "RESERVATIONS_COMPULSORY",
                                "seatMapIsAvailable": false
                            }
                        ],
                        "organisation": {
                            "id": "ENT:Authority:ENT",
                            "name": "Entur"
                        },
                        "group": {
                            "id": "VYT:GroupTicket:Sleep",
                            "version": "VYT:Version:GT-Sleep-1",
                            "description": "",
                            "userProfiles": []
                        },
                        "fareClasses": [
                            "PREMIUM_CLASS"
                        ]
                    },
                    "geographicalValidity": {
                        "serviceJourneys": [
                            {
                                "datedServiceJourneyId": "ABC:DatedServiceJourney:1234_ABC-DEF_25-05-25",
                                "serviceJourneyId": "ABC:ServiceJourney:123"
                            }
                        ],
                        "pointToPointValidity": {
                            "fromPlace": "NSR:StopPlace:337",
                            "toPlace": "NSR:StopPlace:548"
                        }
                    }
                }
            ],
            "travellerMapping": [
                {
                    "travellerIds": [
                        "A",
                        "C"
                    ],
                    "maxNumberOfTravellers": 2,
                    "minNumberOfTravellers": 2,
                    "userType": "ADULT"
                }
            ],
            "available": 5
        }, ...
    ],
    "unavailableProducts": []
}`}
    </pre>
    </ExpandablePanel>
    <br />
    <h3 {...{
      "id": "search-by-stop-places"
    }}>{`Search by stop-places`}</h3>
    <p><a parentName="p" {...{
        "href": "#table-of-contents"
      }}>{`(back to Table of contents)`}</a>{` `}<br />{`
`}<a parentName="p" {...{
        "href": "https://petstore.swagger.io/?url=https://api.entur.io/api-docs/offers#/stop-places/postOffersV2SearchStopPlaces"
      }}>{`Swagger documentation`}</a></p>
    <p>{`Use the stop-places endpoint if you want to request offers that are not associated with any specific departure.
The offers returned are period tickets or single tickets that are valid between stop places and for a given duration.
Some combinations of stop places may result in offers that cover parts of the distance.
As an example, let's say the "from" stop place is A, and the "to" stop place is C.
There may not be a ticket that is valid from A to C, but tickets that are valid from A to B, and B to C.
In that case you will receive offers that are valid for A to B and B to C.
If you only want offers that cover the whole distance, set `}<inlineCode parentName="p">{`offersForWholeTripOnly`}</inlineCode>{` to true.`}</p>
    <h4 {...{
      "id": "examplerequest-to-searchstop-places"
    }}>{`ExampleRequest to search/stop-places`}</h4>
    <p>{`A basic request to the stop-places endpoint may contain `}<inlineCode parentName="p">{`travelDate`}</inlineCode>{`, `}<inlineCode parentName="p">{`from`}</inlineCode>{`, `}<inlineCode parentName="p">{`to`}</inlineCode>{`, and `}<inlineCode parentName="p">{`travellers`}</inlineCode>{` (including `}<inlineCode parentName="p">{`userType`}</inlineCode>{` or `}<inlineCode parentName="p">{`age`}</inlineCode>{`).`}</p>
    <ExpandablePanel title="ExampleRequest to search/stop-places" mdxType="ExpandablePanel">
    <pre>{`
        "travellers": [
            {
                "userType": "ADULT"
            }
        ],
        "travelDate": "2022-02-24T00:00:00+00",
        "from": "NSR:StopPlace:337",
        "to": "NSR:StopPlace:421",
        "offersForWholeTripOnly": false
    `}</pre>
    </ExpandablePanel>
    <h4 {...{
      "id": "exampleresponse-from-searchstop-places"
    }}>{`ExampleResponse from search/stop-places`}</h4>
    <ExpandablePanel title="ExampleResponse from search/stop-places" mdxType="ExpandablePanel">
    <pre>{`
        {
            "offers": [
                {
                    "id": "c76b31fa-2cd8-4415-aa6c-ff3c1e761a32",
                    "name": "365-dagersbillett",
                    "description": "",
                    "price": {
                        "amount": "100020.00",
                        "currency": "NOK"
                    },
                    "geographicalValidity": {
                        "pointToPointValidity": {
                            "fromPlace": "NSR:StopPlace:337",
                            "toPlace": "NSR:StopPlace:421"
                        }
                    },
                    "preassignedProducts": [
                        {
                            "id": "ABC:PreassignedFareProduct:365days",
                            "version": "ABC:Version:Pfp365days-1",
                            "name": "365-dagersbillett",
                            "description": "",
                            "properties": {
                                "isExchangeable": true,
                                "isRefundable": false,
                                "userProfileId": "ABC:UserProfile:Adult",
                                "userProfileVersion": "ABC:Version:V1",
                                "organisation": {
                                    "id": "ENT:Authority:ENT",
                                    "name": "Entur"
                                },
                                "duration": "PT8760H",
                                "fareClasses": [
                                    "STANDARD_CLASS"
                                ]
                            },
                            "geographicalValidity": {
                                "pointToPointValidity": {
                                    "fromPlace": "NSR:StopPlace:337",
                                    "toPlace": "NSR:StopPlace:421"
                                }
                            }
                        }
                    ],
                    "travellerMapping": [
                        {
                            "travellerIds": [
                                "871c6e3b-7a00-4c5d-8cdf-15e882928593"
                            ],
                            "maxNumberOfTravellers": 1,
                            "minNumberOfTravellers": 1,
                            "userType": "ADULT"
                        }
                    ]
                },
                {
                    "id": "5ef7c6a2-17e3-45cc-bcba-ebcbecb097df",
                    "name": "30-dagersbillett",
                    "description": "",
                    "price": {
                        "amount": "10002.00",
                        "currency": "NOK"
                    },
                    "geographicalValidity": {
                        "pointToPointValidity": {
                            "fromPlace": "NSR:StopPlace:337",
                            "toPlace": "NSR:StopPlace:421"
                        }
                    },
                    "preassignedProducts": [
                        {
                            "id": "ABC:PreassignedFareProduct:30days",
                            "version": "ABC:Version:Pfp30days-1",
                            "name": "30-dagersbillett",
                            "description": "",
                            "properties": {
                                "isExchangeable": true,
                                "isRefundable": false,
                                "userProfileId": "ABC:UserProfile:Adult",
                                "userProfileVersion": "ABC:Version:V1",
                                "organisation": {
                                    "id": "ENT:Authority:ENT",
                                    "name": "Entur"
                                },
                                "duration": "PT720H",
                                "fareClasses": [
                                    "STANDARD_CLASS"
                                ]
                            },
                            "geographicalValidity": {
                                "pointToPointValidity": {
                                    "fromPlace": "NSR:StopPlace:337",
                                    "toPlace": "NSR:StopPlace:421"
                                }
                            }
                        }
                    ],
                    "travellerMapping": [
                        {
                            "travellerIds": [
                                "871c6e3b-7a00-4c5d-8cdf-15e882928593"
                            ],
                            "maxNumberOfTravellers": 1,
                            "minNumberOfTravellers": 1,
                            "userType": "ADULT"
                        }
                    ]
                }, ...
            ]
        }
        `}</pre>
    </ExpandablePanel>
    <br />
    <h3 {...{
      "id": "search-by-zones"
    }}>{`Search by zones`}</h3>
    <p><a parentName="p" {...{
        "href": "#table-of-contents"
      }}>{`(back to Table of contents)`}</a>{` `}<br />{`
`}<a parentName="p" {...{
        "href": "https://petstore.swagger.io/?url=https://api.entur.io/api-docs/offers#/zones/postOffersV2SearchZones"
      }}>{`Swagger documentation`}</a></p>
    <p>{`Use the `}<inlineCode parentName="p">{`search/zones`}</inlineCode>{` endpoint if you want to get offers for products that have validity based on zones specified.
As with the `}<inlineCode parentName="p">{`search/stop-places`}</inlineCode>{` endpoint, the `}<inlineCode parentName="p">{`search/zones`}</inlineCode>{` endpoint responds with products that are not limited to a specific departure.
From `}<inlineCode parentName="p">{`search/zones`}</inlineCode>{` you will receive single tickets and period tickets that are valid in the zones specified and for the duration specified in the period ticket.`}</p>
    <p>{`When a traveller already own a valid ticket, the fields `}<inlineCode parentName="p">{`productIds`}</inlineCode>{` (when there can be one or more travellers) or `}<inlineCode parentName="p">{`existingTicket`}</inlineCode>{` (only for when there is only one traveller) can be used.`}</p>
    <h4 {...{
      "id": "examplerequests-to-searchzones"
    }}>{`ExampleRequests to search/zones`}</h4>
    <p>{`A basic request to the zones endpoint may contain `}<inlineCode parentName="p">{`travelDate`}</inlineCode>{`, `}<inlineCode parentName="p">{`from`}</inlineCode>{`, `}<inlineCode parentName="p">{`to`}</inlineCode>{`, and `}<inlineCode parentName="p">{`travellers`}</inlineCode>{` (including `}<inlineCode parentName="p">{`userType`}</inlineCode>{` or `}<inlineCode parentName="p">{`age`}</inlineCode>{`).`}</p>
    <ExpandablePanel title="ExampleRequest to search/zones" mdxType="ExpandablePanel">
    <pre>{`
    {
        "from": "ABC:FareZone:4",
        "to": "ABC:FareZone:3",
        "travelDate": "2025-09-20T12:00:00Z",
        "travellers": [
            {
                "userType": "ADULT"
            }
        ]
    }
    `}</pre>
    </ExpandablePanel>
    <ExpandablePanel title="ExampleRequest to search/zones with SaleDiscountRight specified in productIds" mdxType="ExpandablePanel">
    <pre>{`
    {
        "from": "ABC:FareZone:4",
        "to": "ABC:FareZone:3",
        "travelDate": "2025-09-20T12:00:00Z",
        "travellers": [
            {
                "userType": "ADULT",
                "productIds": [
                    "ABC:SaleDiscountRight:Discount"
                ]
            }
        ]
    }
    `}</pre>
    </ExpandablePanel>
    <ExpandablePanel title="ExampleRequest to search/zones with Season ticket specified in existingTicket" mdxType="ExpandablePanel">
    <pre>{`
    {
        "from": "ABC:FareZone:4",
        "to": "ABC:FareZone:3",
        "travelDate": "2025-09-20T12:00:00Z",
        "travellers": [
            {
                "userType": "ADULT"
            }
        ],
        "existingTicket": {
            "id": "ABC:PreassignedFareProduct:753c4f14",
            "zones": [
                "ABC:FareZone:4"
            ]
        }
    }
    `}</pre>
    </ExpandablePanel>
    <h4 {...{
      "id": "exampleresponses-from-searchzones"
    }}>{`ExampleResponses from search/zones`}</h4>
    <ExpandablePanel title="ExampleResponse to search/zones" mdxType="ExpandablePanel">
    <pre>{`
        {
        "offers": [
            {
            "id": "3b8c753e-5866-49ca-95b5-47bf4ec060b2",
            "name": "Enkeltbillett",
            "description": "Enkeltbillett",
            "price": {
                "amount": "72.00",
                "currency": "NOK"
            },
            "availableFulfillmentMethods": [
                {
                    "id": "ENT:FulfilmentMethod:IdBasert",
                    "version": "ENT:Version:FM-IdBasert-1",
                    "distributionRights": [
                        "SELL",
                        "REFUND"
                    ]
                },
                {
                    "id": "ENT:FulfilmentMethod:FysiskBillett",
                    "version": "ENT:Version:FM-FysiskBillett-1",
                    "distributionRights": [
                        "SELL",
                        "REFUND"
                    ]
                }
            ],
            "geographicalValidity": {
                "zonalValidity": {
                    "fareZones": [
                        "ABC:FareZone:4",
                        "ABC:FareZone:3"
                    ]
                }
            },
            "preassignedProducts": [
                {
                    "id": "ABC:PreassignedFareProduct:58aec6a8",
                    "version": "ABC:Version:FP-f1d831f7-bcef-49bb-8779-ea6266da91e2",
                    "name": "Enkeltbillett",
                    "description": "",
                    "properties": {
                        "isExchangeable": false,
                        "isRefundable": false,
                        "userProfileSummary": {
                            "id": "ABC:UserProfile:Adult",
                            "version": "ABC:Version:UP-Adult",
                            "name": "Voksen",
                            "userType": "ADULT"
                        },
                        "organisation": {
                            "id": "ENT:Authority:ENT",
                            "name": "Entur"
                        },
                        "duration": "PT1H30M",
                        "fareClasses": [
                            "STANDARD_CLASS"
                        ],
                        "durationType": "SINGLE_TRIP"
                    },
                    "geographicalValidity": {
                        "zonalValidity": {
                            "fareZones": [
                                "ABC:FareZone:4",
                                "ABC:FareZone:3"
                            ]
                        }
                    }
                }
            ],
            "optionalProductsSelectableIds": [],
            "travellerMapping": [
                {
                    "travellerIds": [
                        "943eb7cd-3e4a-4bdb-86d4-105fe1242093"
                    ],
                    "maxNumberOfTravellers": 1,
                    "minNumberOfTravellers": 1,
                    "userType": "ADULT"
                }
            ]
        }
        `}</pre>
    </ExpandablePanel>
    <ExpandablePanel title="ExampleResponse to search/zones with SaleDiscountRight specified in productIds" mdxType="ExpandablePanel">
    <pre>{`
        {
            "offers": [
                {
                    "id": "486e6afc-862d-4267-a14e-c7b1fd00a42e",
                    "name": "Enkeltbillett",
                    "description": "Enkeltbillett",
                    "price": {
                        "amount": "64.80",
                        "currency": "NOK"
                    },
                    "availableFulfillmentMethods": [
                        {
                            "id": "ENT:FulfilmentMethod:IdBasert",
                            "version": "ENT:Version:FM-IdBasert-1",
                            "distributionRights": [
                                "SELL",
                                "REFUND"
                            ]
                        },
                        {
                            "id": "ENT:FulfilmentMethod:FysiskBillett",
                            "version": "ENT:Version:FM-FysiskBillett-1",
                            "distributionRights": [
                                "SELL",
                                "REFUND"
                            ]
                        }
                    ],
                    "geographicalValidity": {
                        "zonalValidity": {
                            "fareZones": [
                                "ABC:FareZone:4",
                                "ABC:FareZone:3"
                            ]
                        }
                    },
                    "preassignedProducts": [
                        {
                            "id": "ABC:PreassignedFareProduct:58aec6a8",
                            "version": "ABC:Version:FP-f1d831f7-bcef-49bb-8779-ea6266da91e2",
                            "name": "Enkeltbillett",
                            "description": "",
                            "properties": {
                                "isExchangeable": false,
                                "isRefundable": false,
                                "userProfileSummary": {
                                    "id": "ABC:UserProfile:Adult",
                                    "version": "ABC:Version:UP-Adult",
                                    "name": "Voksen",
                                    "userType": "ADULT"
                                },
                                "organisation": {
                                    "id": "ENT:Authority:ENT",
                                    "name": "Entur"
                                },
                                "duration": "PT1H30M",
                                "fareClasses": [
                                    "STANDARD_CLASS"
                                ],
                                "durationType": "SINGLE_TRIP"
                            },
                            "geographicalValidity": {
                                "zonalValidity": {
                                    "fareZones": [
                                        "ABC:FareZone:4",
                                        "ABC:FareZone:3"
                                    ]
                                }
                            },
                            "discountRight": {
                                "id": "ABC:SaleDiscountRight:Discount",
                                "version": "ABC:Version:FP-16e9e722-bb45-4040-a76a-abeedec63bdb",
                                "price": {
                                    "amount": "-7.20",
                                    "currency": "NOK"
                                },
                                "name": "Reis 10%",
                                "description": "Reis 10%"
                            }
                        }
                    ],
                    "optionalProductsSelectableIds": [],
                    "travellerMapping": [
                        {
                            "travellerIds": [
                                "c6120835-1e07-476f-a8e1-3d23373288b6"
                            ],
                            "maxNumberOfTravellers": 1,
                            "minNumberOfTravellers": 1,
                            "userType": "ADULT"
                        }
                    ]
                }
        `}</pre>
    </ExpandablePanel>
    <ExpandablePanel title="ExampleResponse to search/zones with Season ticket specified in existingTicket" mdxType="ExpandablePanel">
    <pre>{`
        {
            "offers": [
                {
                    "id": "dc3d5d46-6067-4ac1-a562-cb4e3ae0c8ee",
                    "name": "Enkeltbillett",
                    "description": "Enkeltbillett",
                    "price": {
                        "amount": "44.00",
                        "currency": "NOK"
                    },
                    "availableFulfillmentMethods": [
                        {
                            "id": "ENT:FulfilmentMethod:IdBasert",
                            "version": "ENT:Version:FM-IdBasert-1",
                            "distributionRights": [
                                "SELL",
                                "REFUND"
                            ]
                        },
                        {
                            "id": "ENT:FulfilmentMethod:FysiskBillett",
                            "version": "ENT:Version:FM-FysiskBillett-1",
                            "distributionRights": [
                                "SELL",
                                "REFUND"
                            ]
                        }
                    ],
                    "geographicalValidity": {
                        "zonalValidity": {
                            "fareZones": [
                                "ABC:FareZone:3"
                            ]
                        }
                    },
                    "preassignedProducts": [
                        {
                            "id": "ABC:PreassignedFareProduct:58aec6a8",
                            "version": "ABC:Version:FP-f1d831f7-bcef-49bb-8779-ea6266da91e2",
                            "name": "Enkeltbillett",
                            "description": "",
                            "properties": {
                                "isExchangeable": false,
                                "isRefundable": false,
                                "userProfileSummary": {
                                    "id": "ABC:UserProfile:Adult",
                                    "version": "ABC:Version:UP-Adult",
                                    "name": "Voksen",
                                    "userType": "ADULT"
                                },
                                "organisation": {
                                    "id": "ENT:Authority:ENT",
                                    "name": "Entur"
                                },
                                "duration": "PT1H",
                                "fareClasses": [
                                    "STANDARD_CLASS"
                                ],
                                "durationType": "SINGLE_TRIP"
                            },
                            "geographicalValidity": {
                                "zonalValidity": {
                                    "fareZones": [
                                        "ABC:FareZone:3"
                                    ]
                                }
                            }
                        }
                    ],
                    "optionalProductsSelectableIds": [],
                    "travellerMapping": [
                        {
                            "travellerIds": [
                                "8b607a11-b252-4b6a-86b5-3e7ca4154b5d"
                            ],
                            "maxNumberOfTravellers": 1,
                            "minNumberOfTravellers": 1,
                            "userType": "ADULT"
                        }
                    ]
                },
                {
                    "id": "047a043c-d6e4-492d-ae1a-72d627b99343",
                    "name": "Tilleggsbillett",
                    "description": "Tilleggsbillett",
                    "price": {
                        "amount": "44.00",
                        "currency": "NOK"
                    },
                    "availableFulfillmentMethods": [
                        {
                            "id": "ENT:FulfilmentMethod:FysiskBillett",
                            "version": "ENT:Version:FM-FysiskBillett-1",
                            "distributionRights": [
                                "SELL",
                                "REFUND"
                            ]
                        }
                    ],
                    "geographicalValidity": {
                        "zonalValidity": {
                            "fareZones": [
                                "ABC:FareZone:3"
                            ]
                        }
                    },
                    "preassignedProducts": [
                        {
                            "id": "ABC:PreassignedFareProduct:49408261",
                            "version": "ABC:Version:FP-46aea9c4-4c94-4b0c-b50f-32570920a550",
                            "name": "Billett for ekstra soner",
                            "description": "",
                            "properties": {
                                "isExchangeable": false,
                                "isRefundable": true,
                                "userProfileSummary": {
                                    "id": "ABC:UserProfile:Adult",
                                    "version": "ABC:Version:UP-Adult",
                                    "name": "Voksen",
                                    "userType": "ADULT"
                                },
                                "organisation": {
                                    "id": "ENT:Authority:ENT",
                                    "name": "Entur"
                                },
                                "duration": "PT1H30M",
                                "fareClasses": [
                                    "STANDARD_CLASS"
                                ],
                                "durationType": "SINGLE_TRIP"
                            },
                            "geographicalValidity": {
                                "zonalValidity": {
                                    "fareZones": [
                                        "ABC:FareZone:3"
                                    ]
                                }
                            }
                        }
                    ],
                    "optionalProductsSelectableIds": [],
                    "travellerMapping": [
                        {
                            "travellerIds": [
                                "d7a05de3-fa08-4d14-ba80-7f10afe9beac"
                            ],
                            "maxNumberOfTravellers": 1,
                            "minNumberOfTravellers": 1,
                            "userType": "ADULT"
                        }
                    ]
                }
            ]
        }
        `}</pre>
    </ExpandablePanel>
    <br />
    <h3 {...{
      "id": "search-by-authority"
    }}>{`Search by authority`}</h3>
    <p><a parentName="p" {...{
        "href": "#table-of-contents"
      }}>{`(back to Table of contents)`}</a>{` `}<br />{`
`}<a parentName="p" {...{
        "href": "https://petstore.swagger.io/?url=https://api.entur.io/api-docs/offers#/authority/postOffersV2SearchAuthority"
      }}>{`Swagger documentation`}</a></p>
    <p>{`Use the `}<inlineCode parentName="p">{`search/authority`}</inlineCode>{` endpoint if you want to get offers for products with a static geographical validity, using only a traveller and authority as input.
Only constraints that will have an affect are Purchase Window, Authority, Distribution Channel and User Profile, everything else will be ignored when evaluating the product definition.`}</p>
    <h4 {...{
      "id": "examplerequests-to-searchauthority"
    }}>{`ExampleRequests to search/authority`}</h4>
    <p>{`A basic request to the authority endpoint may contain `}<inlineCode parentName="p">{`travelDate`}</inlineCode>{`, `}<inlineCode parentName="p">{`authorityId`}</inlineCode>{`, and `}<inlineCode parentName="p">{`travellers`}</inlineCode>{` (including `}<inlineCode parentName="p">{`userType`}</inlineCode>{` or `}<inlineCode parentName="p">{`age`}</inlineCode>{`).`}</p>
    <ExpandablePanel title="ExampleRequest to search/authority" mdxType="ExpandablePanel">
    <pre>{`
    {
        "travelDate": "2022-10-15T03:00:00.000Z",
        "travellers": [
            {
                "userType": "ADULT"
            }
        ],
        "authorityId": "ENT:Authority:ENT"
    }
    `}</pre>
    </ExpandablePanel>
    <h4 {...{
      "id": "exampleresponses-from-searchauthority"
    }}>{`ExampleResponses from search/authority`}</h4>
    <ExpandablePanel title="ExampleResponse from search/authority" mdxType="ExpandablePanel">
    <pre>{`
        {
            "offers": [
                {
                    "id": "4b2fe5c8-eb81-4015-8a3a-0ed32861e4d2",
                    "name": "Natt 2",
                    "description": "Natt 2",
                    "price": {
                        "amount": "150.00",
                        "currency": "NOK"
                    },
                    "availableFulfillmentMethods": [
                        {
                            "id": "ENT:FulfilmentMethod:IdBasert",
                            "version": "ENT:Version:FM-IdBasert-1"
                        }
                    ],
                    "geographicalValidity": {},
                    "preassignedProducts": [
                        {
                            "id": "ATB:PreassignedFareProduct:877baeff",
                            "version": "ATB:Version:c463bb9c-7e63-4c84-a07b-837dc264694a",
                            "name": "Natt 2",
                            "description": "Natt 2",
                            "properties": {
                                "isExchangeable": false,
                                "isRefundable": false,
                                "userProfileSummary": {
                                    "id": "ATB:UserProfile:8ee842e3",
                                    "version": "ATB:Version:230ef468-95a1-461a-b109-104ac046cb0d",
                                    "name": "Voksen",
                                    "userType": "ADULT"
                                },
                                "organisation": {
                                    "id": "ENT:Authority:ENT",
                                    "name": "AtB"
                                },
                                "duration": "PT1H30M",
                                "fareClasses": [
                                    "STANDARD_CLASS"
                                ],
                                "durationType": "SINGLE_TRIP"
                            },
                            "geographicalValidity": {}
                        }
                    ],
                    "travellerMapping": [
                        {
                            "travellerIds": [
                                "3ece9584-4e03-4536-90ce-0037893b5e88"
                            ],
                            "maxNumberOfTravellers": 1,
                            "minNumberOfTravellers": 1,
                            "userType": "ADULT"
                        }
                    ]
                },
                {
                    "id": "db2d4e67-bd0c-40d6-afc0-4e016566809b",
                    "name": "Natt 3",
                    "description": "Natt 3",
                    "price": {
                        "amount": "200.00",
                        "currency": "NOK"
                    },
                    "availableFulfillmentMethods": [
                        {
                            "id": "ENT:FulfilmentMethod:IdBasert",
                            "version": "ENT:Version:FM-IdBasert-1"
                        }
                    ],
                    "geographicalValidity": {},
                    "preassignedProducts": [
                        {
                            "id": "ATB:PreassignedFareProduct:e317a807",
                            "version": "ATB:Version:ffdc03c6-2003-429f-a0bc-b8ddaa6b8f9d",
                            "name": "Natt 3",
                            "description": "Natt 3",
                            "properties": {
                                "isExchangeable": false,
                                "isRefundable": false,
                                "userProfileSummary": {
                                    "id": "ATB:UserProfile:8ee842e3",
                                    "version": "ATB:Version:230ef468-95a1-461a-b109-104ac046cb0d",
                                    "name": "Voksen",
                                    "userType": "ADULT"
                                },
                                "organisation": {
                                    "id": "ENT:Authority:ENT",
                                    "name": "AtB"
                                },
                                "duration": "PT1H30M",
                                "fareClasses": [
                                    "STANDARD_CLASS"
                                ],
                                "durationType": "SINGLE_TRIP"
                            },
                            "geographicalValidity": {}
                        }
                    ],
                    "travellerMapping": [
                        {
                            "travellerIds": [
                                "3ece9584-4e03-4536-90ce-0037893b5e88"
                            ],
                            "maxNumberOfTravellers": 1,
                            "minNumberOfTravellers": 1,
                            "userType": "ADULT"
                        }
                    ]
                },
                {
                    "id": "cbe79d7e-99b5-4693-978e-e37ac21a2f0f",
                    "name": "Natt 1",
                    "description": "Natt 1",
                    "price": {
                        "amount": "100.00",
                        "currency": "NOK"
                    },
                    "availableFulfillmentMethods": [
                        {
                            "id": "ENT:FulfilmentMethod:IdBasert",
                            "version": "ENT:Version:FM-IdBasert-1"
                        }
                    ],
                    "geographicalValidity": {},
                    "preassignedProducts": [
                        {
                            "id": "ATB:PreassignedFareProduct:8f351521",
                            "version": "ATB:Version:fecb1f30-5b6a-473c-9987-d8ff1b649560",
                            "name": "Natt 1",
                            "description": "Natt 1",
                            "properties": {
                                "isExchangeable": false,
                                "isRefundable": false,
                                "userProfileSummary": {
                                    "id": "ATB:UserProfile:8ee842e3",
                                    "version": "ATB:Version:230ef468-95a1-461a-b109-104ac046cb0d",
                                    "name": "Voksen",
                                    "userType": "ADULT"
                                },
                                "organisation": {
                                    "id": "ENT:Authority:ENT",
                                    "name": "AtB"
                                },
                                "duration": "PT1H30M",
                                "fareClasses": [
                                    "STANDARD_CLASS"
                                ],
                                "durationType": "SINGLE_TRIP"
                            },
                            "geographicalValidity": {}
                        }
                    ],
                    "travellerMapping": [
                        {
                            "travellerIds": [
                                "3ece9584-4e03-4536-90ce-0037893b5e88"
                            ],
                            "maxNumberOfTravellers": 1,
                            "minNumberOfTravellers": 1,
                            "userType": "ADULT"
                        }
                    ]
                }
            ],
            "explainInfo": {
                "explainNodes": [
                    {
                        "id": "ATB:SalesPackage:55246449",
                        "version": "ATB:Version:35e3b7f3-5158-45ee-9488-9dbc49ff95fe",
                        "reason": "noValidVersion"
                    }
                ]
            }
        }
        `}</pre>
    </ExpandablePanel>
    <br />
    <h3 {...{
      "id": "search-for-personalised-offers"
    }}>{`Search for personalised offers`}</h3>
    <p><a parentName="p" {...{
        "href": "#table-of-contents"
      }}>{`(back to Table of contents)`}</a>{` `}<br /></p>
    <p>{`Travellers might have certain benefits or entitlements that can be used to get discounted offers.
This can be different kinds of campaigns the customer is part of, personalisation programs or other types of benefits.`}</p>
    <h4 {...{
      "id": "campaign-codes"
    }}>{`Campaign codes`}</h4>
    <p><a parentName="p" {...{
        "href": "#table-of-contents"
      }}>{`(back to Table of contents)`}</a>{` `}<br /></p>
    <p>{`The campaign codes are set on the traveller object when searching for offers.
If the code gives access to an entitlement that is valid for the offer, it will be returned with the discounted price.`}</p>
    <ExpandablePanel title="ExampleRequest to search/zones with campaign codes" mdxType="ExpandablePanel">
    <pre>{`
    {
        ...
        "travellers": [
            {
                ...
                "campaignCodes": [
                    "MYCODE"
                ]
            }
        ]
    }
    `}</pre>
    </ExpandablePanel>
    <h4 {...{
      "id": "personalisation-programs"
    }}>{`Personalisation programs`}</h4>
    <p><a parentName="p" {...{
        "href": "#table-of-contents"
      }}>{`(back to Table of contents)`}</a>{` `}<br /></p>
    <p>{`For travellers that are part of a personalisation program, the `}<inlineCode parentName="p">{`search`}</inlineCode>{` endpoints can be used to get discounted offers when the customer is identified by `}<inlineCode parentName="p">{`customerId`}</inlineCode>{`.
The `}<inlineCode parentName="p">{`customerId`}</inlineCode>{` points to the `}<inlineCode parentName="p">{`customerNumber`}</inlineCode>{` of the Entur-customer that is enrolled in a program.`}</p>
    <ExpandablePanel title="ExampleRequest to search/zones with identified customer" mdxType="ExpandablePanel">
    <pre>{`
    {
        ...
        "travellers": [
            {
                ...
                "customerId": "1234567"
            }
        ]
    }
    `}</pre>
    </ExpandablePanel>
    <p>{`If buying an offer will result in the customer gaining points in a personalisation program, it will be included in `}<inlineCode parentName="p">{`eligibleForAccrualInPrograms`}</inlineCode>{`.`}</p>
    <p>{`If a program appears in `}<inlineCode parentName="p">{`availablePrograms`}</inlineCode>{`, the customer may be added to the program in order to be eligible for accrual.
The caller must themselves implement the logic to add customers to personalisation programs, using the `}<a parentName="p" {...{
        "href": "/pages-personalisation-docs-personalisation"
      }}>{`Personalisation API`}</a>{`.`}</p>
    <ExpandablePanel title="ExampleResponse from search/zones with offer eligible for accrual" mdxType="ExpandablePanel">
    <pre>{`{
    "offers": [
        {
            "id": "df2065db-ca7b-4834-bcb7-a9d42de858df",
            "name": "Enkeltbillett",
            "description": "",
            "price": {
                "amount": "32.00",
                "currency": "NOK"
            },
            "geographicalValidity": {
                "zonalValidity": {
                    "fareZones": [
                        "ENT:FareZone:1"
                    ]
                }
            },
            "preassignedProducts": [
                {
                    "id": "ENT:PreassignedFareProduct:SingleTicket",
                    "version": "ENT:Version:V1",
                    "name": "Enkeltbillett",
                    ...
            ],
            "travellerMapping": [
                ...
            ],
            "eligibleForAccrualInPrograms": [
                {
                    "id": "9",
                    "reference": "entur_bonus",
                    "name": "Entur Bonus Program",
                    "infoLink": "https://entur.no/bonus",
                }
            ],
            "availablePrograms": [
                {
                    "id": "9",
                    "reference": "entur_bonus",
                    "name": "Entur Bonus Program",
                    "infoLink": "https://entur.no/bonus",
                }
            ]
        }
    ]
}`}</pre>
    </ExpandablePanel>
    <p>{`The id of the program can in turn be used to get more information about the program or check the customers status through the `}<a parentName="p" {...{
        "href": "/pages-personalisation-docs-personalisation"
      }}>{`Personalisation API`}</a>{`.`}</p>
    <br />
    <h3 {...{
      "id": "personnel-tickets"
    }}>{`Personnel tickets`}</h3>
    <p><a parentName="p" {...{
        "href": "#table-of-contents"
      }}>{`(back to Table of contents)`}</a>{` `}<br /></p>
    <p>{`Some customers have an entitlement that gives them offers with greatly reduced price.
For personnel tickets, the `}<inlineCode parentName="p">{`EntitlementProduct`}</inlineCode>{` needs to be supplied directly in the Offers request, in addition to the `}<inlineCode parentName="p">{`customerId`}</inlineCode>{`.`}</p>
    <ExpandablePanel title="ExampleRequest to search/zones with personnel ticket entitlement" mdxType="ExpandablePanel">
      <pre>{`{
    ...
    "travellers": [
        {
            ...
            "customerId": "1234567",
            "productIds": [
                "ENT:EntitlementProduct:PersonnelTicket"
            ]
        }
    ]
}
`}</pre>
    </ExpandablePanel>
    <p>{`When reserving the offer in the `}<a parentName="p" {...{
        "href": "/pages-sales-reserve-offers-api"
      }}>{`/v1/reserve-offers`}</a>{` Sales API, the `}<inlineCode parentName="p">{`EntitlementProduct`}</inlineCode>{` needs to be supplied in the request as well.`}</p>
    <ExpandablePanel title="ExampleRequest to /v1/reserve-offers with personnel ticket entitlement" mdxType="ExpandablePanel">
    <pre>{`{
    "offerConfigurations": [
        {
            "offerId": "df2065db-ca7b-4834-bcb7-a9d42de858df",
            "customers": [
                {
                    "customerId": "1234567",
                    "entitlements": [
                        {
                            "contractId": "12931728",
                            "entitlementProductRef": {
                                "id": "ENT:EntitlementProduct:PersonnelTicket"
                            }
                         }
                    ]
                }
            ]
        }
    ]
}
`}</pre>
    </ExpandablePanel>
    <h3 {...{
      "id": "discounted-offers"
    }}>{`Discounted offers`}</h3>
    <p><a parentName="p" {...{
        "href": "#table-of-contents"
      }}>{`(back to Table of contents)`}</a>{` `}<br /></p>
    <p>{`Offers that are discounted as a result of an applied entitlement or benefit, will contain information about the discount in the response.
The discount summary is located under `}<inlineCode parentName="p">{`offers[].preassignedProducts[].discountRight`}</inlineCode>{` in the Offers response.`}</p>
    <ExpandablePanel title="ExampleResponse from search/zones with discounted offer" mdxType="ExpandablePanel">
    <pre>{`{
    "offers": [
        {
            "id": "df2065db-ca7b-4834-bcb7-a9d42de858df",
            "name": "Enkeltbillett",
            "description": "",
            "price": {
                "amount": "12.00",
                "currency": "NOK"
            },
            "preassignedProducts": [
                {
                    "id": "ENT:PreassignedFareProduct:SingleTicket",
                    "version": "ENT:Version:V1",
                    "name": "Enkeltbillett",
                    ...,
                    "discountRight": {
                        "id": "ENT:SaleDiscountRight:SomeDiscount",
                        "version": "ENT:Version:V1",
                        "price": {
                            "amount": "20.00",
                            "currency": "NOK"
                        },
                        "name": "Some Discount",
                        "description": "Some discount",
                        "originatingFromProductId": "ENT:EntitlementProduct:PersonnelTicket"
                    }
            ],
            "travellerMapping": [
                ...
            ],
        }
    ]
}`}</pre>
    </ExpandablePanel>
    <br />
    <h2 {...{
      "id": "search-response-overview"
    }}>{`Search Response Overview`}</h2>
    <p><a parentName="p" {...{
        "href": "#table-of-contents"
      }}>{`(back to Table of contents)`}</a></p>
    <p>{`All Offers endpoints return a `}<inlineCode parentName="p">{`SearchResponse`}</inlineCode>{`, which may contain one or multiple `}<inlineCode parentName="p">{`OfferSummary`}</inlineCode>{`. In addition, the response may include one or more components such as `}<inlineCode parentName="p">{`Recommendation`}</inlineCode>{` (see `}<a parentName="p" {...{
        "href": "https://developer.entur.org/pages-recommendations-guide"
      }}>{`Recommendations Guide`}</a>{`), `}<inlineCode parentName="p">{`OptionalProduct`}</inlineCode>{`, and `}<inlineCode parentName="p">{`UnavailableProduct`}</inlineCode>{`.`}</p>
    <p><inlineCode parentName="p">{`OfferSummary`}</inlineCode>{` presents one or more valid offers. Each offer includes details such as its name, description, price, the products it contains, geographical validity, and available fulfillment methods.`}</p>
    <h3 {...{
      "id": "unavailable-products"
    }}>{`Unavailable Products`}</h3>
    <p><a parentName="p" {...{
        "href": "#table-of-contents"
      }}>{`(back to Table of contents)`}</a></p>
    <p>{`The `}<inlineCode parentName="p">{`unavailableProducts`}</inlineCode>{` array in the search response lists products that `}<em parentName="p">{`would`}</em>{` have been included in an offer if they were available. Only products that the distribution channel is authorised to sell are included. This is useful for understanding why a product the traveller might expect to see is not present in the offers.`}</p>
    <p>{`Each `}<inlineCode parentName="p">{`UnavailableProduct`}</inlineCode>{` contains the product ID and an `}<inlineCode parentName="p">{`unavailableStatus`}</inlineCode>{` field explaining why the product is unavailable:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Status`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`SOLD_OUT`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`No more seats or quotas available for sale`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`CLOSED`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The product is closed for sale`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`NOT_IN_PURCHASE_WINDOW`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Outside of the window where the product can be purchased`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`NOT_YET_AVAILABLE`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The product is not available yet`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`NO_LONGER_AVAILABLE`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The product is no longer available for purchase`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`NO_QUOTAS_DEFINED`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The product requires quotas but none are defined yet`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`UNKNOWN`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Unavailable for an unspecified reason`}</td>
        </tr>
      </tbody>
    </table>
    <ExpandablePanel title="Example response with unavailableProducts" mdxType="ExpandablePanel">
      <pre>{`"unavailableProducts": [
    {
        "id": "ABC:SalesPackage:1234abcd",
        "status": "UNKNOWN",
        "unavailableStatus": "NOT_YET_AVAILABLE",
        "serviceJourney": {
            "serviceJourneyId": "ABC:ServiceJourney:123",
            "datedServiceJourneyId": "ABC:DatedServiceJourney:123_ABC-DEF_26-06-25"
        },
        "properties": {
            "accommodations": [
                "RECLINING_SEAT"
            ],
            "fareClasses": [
                "PREMIUM_CLASS"
            ],
            "isExchangeable": false,
            "isRefundable": false
        }
    }
]`}</pre>
    </ExpandablePanel>
    <BannerAlertBox title="Deprecation notice" variant="info" mdxType="BannerAlertBox">
    The older <code>status</code> field (with values <code>SOLD_OUT</code>, <code>CLOSED</code>, <code>UNKNOWN</code>) is deprecated.
    Use <code>unavailableStatus</code> instead, which provides more specific reasons for why a product is unavailable.
    </BannerAlertBox>
    <h3 {...{
      "id": "geographical-validity"
    }}>{`Geographical Validity`}</h3>
    <p><a parentName="p" {...{
        "href": "#table-of-contents"
      }}>{`(back to Table of contents)`}</a></p>
    <p>{`Geographical Validity is a component of the search response and may appear in multiple sections. It describes the geographical validity for where an offer, product or recommendation is valid.
This includes validity on `}<strong parentName="p">{`Service Journeys`}</strong>{`, `}<strong parentName="p">{`Point To Point Validity`}</strong>{`, and `}<strong parentName="p">{`Zonal Validity`}</strong>{`. In cases of `}<em parentName="p">{`replacement transport`}</em>{`, these fields describe the replacement journey.`}</p>
    <h3 {...{
      "id": "service-journeys"
    }}>{`Service Journeys`}</h3>
    <p>{`The `}<inlineCode parentName="p">{`serviceJourneys`}</inlineCode>{` field specifies the Service Journey and Dated Service Journey on which the product is valid.`}</p>
    <ExpandablePanel title="Example geographicalValidity with serviceJourneys" mdxType="ExpandablePanel">
      <pre>{`
"geographicalValidity":{
   "serviceJourneys":[
      {
         "datedServiceJourneyId":"ABC:DatedServiceJourney:1234_ABC-DEF_25-05-25",
         "serviceJourneyId":"ABC:ServiceJourney:123"
      }
   ]
}`}</pre>
    </ExpandablePanel>
    <h3 {...{
      "id": "point-to-point-validity"
    }}>{`Point To Point Validity`}</h3>
    <p>{`The `}<inlineCode parentName="p">{`pointToPointValidity`}</inlineCode>{` field specifies the geographical start and stop places between which the product is valid.`}</p>
    <ExpandablePanel title="Example geographicalValidity with pointToPointValidity" mdxType="ExpandablePanel">
      <pre>{`
"geographicalValidity":{
   "pointToPointValidity":{
      "fromPlace":"NSR:StopPlace:337",
      "toPlace":"NSR:StopPlace:421"
   }
}`}</pre>
    </ExpandablePanel>
    <h3 {...{
      "id": "zonal-validity"
    }}>{`Zonal Validity`}</h3>
    <p>{`The `}<inlineCode parentName="p">{`zonalValidity`}</inlineCode>{` field specifies the geographical zones in which the product is valid. The `}<inlineCode parentName="p">{`fareZones`}</inlineCode>{` field lists the specific fare zones where the product is valid, based on the zones the trip traverses.
The `}<inlineCode parentName="p">{`namedGroupOfTariffZones`}</inlineCode>{` field lists the Group of Tariff Zones that define the complete area of validity through the fare zones contained in these groups.
If Zonal Validity includes `}<inlineCode parentName="p">{`namedGroupOfTariffZones`}</inlineCode>{`, the group with its zones represents the full area of validity for the product, and the `}<inlineCode parentName="p">{`fareZones`}</inlineCode>{` field is redundant and may be ignored.
`}<em parentName="p">{`(In Offers v3, `}<inlineCode parentName="em">{`fareZones`}</inlineCode>{` will not be listed when a namedGroupOfTariffZones is defined, since this encompasses all applicable fare zones by default.)`}</em></p>
    <ExpandablePanel title="Example geographicalValidity with zonalValidity" mdxType="ExpandablePanel">
      <pre>{`
"geographicalValidity":{
   "zonalValidity":{
      "fareZones":[
         "ABC:FareZone:1",
         "ABC:FareZone:2"
      ],
      "namedGroupOfTariffZones":[
         {
            "id":"ABC:GroupOfTariffZones:1",
            "name":"Alle Soner"
         }
      ]
   }
}`}</pre>
    </ExpandablePanel>
    <br />
    <h2 {...{
      "id": "offline-clients"
    }}>{`Offline clients`}</h2>
    <p><a parentName="p" {...{
        "href": "#table-of-contents"
      }}>{`(back to Table of contents)`}</a>{` `}<br /></p>
    <p>{`For clients that have poor or no access to the internet, we offer a solution where the client can download a set of data files that can be used to generate offers offline. Access to this functionality is given on request.`}</p>
    <p>{`In essence, there are two different approaches, both sharing the same limitations.
All methods return `}<inlineCode parentName="p">{`SearchResponseFull`}</inlineCode>{`, which is more detailed than the regular search endpoints so that the client may build their own orders.
Offers created this way `}<em parentName="p">{`cannot`}</em>{` be used through the regular sales flow at Entur (`}<a parentName="p" {...{
        "href": "/pages-sales-guides#reserve-offer"
      }}>{`/reserve-offers`}</a>{`).`}</p>
    <h3 {...{
      "id": "offline-search"
    }}>{`Offline search`}</h3>
    <p>{`If the client wishes to get offers for specific trips, zones, stopPlaces etc, they can use the offline versions of the `}<a parentName="p" {...{
        "href": "#search-overview"
      }}>{`search endpoints`}</a>{`. This approach gives the client full control over what they wish to request.
The offline versions works exactly like their online counterparts, except for the limitations outlined above.
All endpoints are also found in the `}<a parentName="p" {...{
        "href": "https://petstore.swagger.io/?url=https://api.entur.io/api-docs/offers#/offline"
      }}>{`api spec`}</a></p>
    <ul>
      <li parentName="ul">{`Instead of `}<inlineCode parentName="li">{`/offers/v2/search/trip-pattern`}</inlineCode>{`, use `}<inlineCode parentName="li">{`/offers/v2/offline-cache/trip-pattern`}</inlineCode></li>
      <li parentName="ul">{`Instead of `}<inlineCode parentName="li">{`/offers/v2/search/zones`}</inlineCode>{`, use `}<inlineCode parentName="li">{`/offers/v2/offline-cache/zones`}</inlineCode></li>
      <li parentName="ul">{`Instead of `}<inlineCode parentName="li">{`/offers/v2/search/stop-places`}</inlineCode>{`, use `}<inlineCode parentName="li">{`/offers/v2/offline-cache/stop-places`}</inlineCode></li>
      <li parentName="ul">{`Instead of `}<inlineCode parentName="li">{`/offers/v2/search/authority`}</inlineCode>{`, use `}<inlineCode parentName="li">{`/offers/v2/offline-cache/authority`}</inlineCode></li>
    </ul>
    <h3 {...{
      "id": "servicejourney-with-stopplaces"
    }}>{`ServiceJourney with StopPlaces`}</h3>
    <p><a parentName="p" {...{
        "href": "https://petstore.swagger.io/?url=https://api.entur.io/api-docs/offers#/offline/post-offers-v2-offline-cache-sj-stops"
      }}>{`Swagger documentation`}</a></p>
    <p>{`Generates offers for all subsequent pairs of stop places on the given ServiceJourney, for UserTypes `}<inlineCode parentName="p">{`ADULT`}</inlineCode>{`, `}<inlineCode parentName="p">{`CHILD`}</inlineCode>{`, `}<inlineCode parentName="p">{`INFANT`}</inlineCode>{`, `}<inlineCode parentName="p">{`SENIOR`}</inlineCode>{`, `}<inlineCode parentName="p">{`YOUTH`}</inlineCode>{`, `}<inlineCode parentName="p">{`STUDENT`}</inlineCode>{` and `}<inlineCode parentName="p">{`MILITARY`}</inlineCode>{`.
The products one can expect are restricted to single tickets without `}<a parentName="p" {...{
        "href": "/pages-inventory-intro"
      }}>{`quotas`}</a>{`, with no `}<a parentName="p" {...{
        "href": "/pages-seating-intro"
      }}>{`capacity requirements`}</a>{`, no `}<a parentName="p" {...{
        "href": "#search-for-personalised-offers"
      }}>{`personalisation`}</a>{` and no `}<a parentName="p" {...{
        "href": "/pages-recommendations-guide"
      }}>{`recommendations`}</a>{`.
In addition, only distance based products are supported. Do NOT use for zone based pricing.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      